import React, { useState, useEffect, useMemo, useRef } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Chip
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { V4_T, inferPropertyEditDefaults, getBillingGroupOverlapConflicts } from './propertyDetailHelpers'
import BillingGroupMemberPicker from './BillingGroupMemberPicker'
import BillingGroupConflictNotice from './BillingGroupConflictNotice'

// Solver operates Mon-Fri only; a day can be locked in (required) or merely
// favored (preferred), never both — toggling a day Required clears it from
// Preferred elsewhere in this file.
const SERVICE_DAYS = [
  { value: 'Monday', label: 'Mon' },
  { value: 'Tuesday', label: 'Tue' },
  { value: 'Wednesday', label: 'Wed' },
  { value: 'Thursday', label: 'Thu' },
  { value: 'Friday', label: 'Fri' }
]

// Frequency options shared by the maintenance / irrigation / herbicide selects.
// Value 'none' represents "not serviced" so the field is never left ambiguous.
const PROPERTY_FREQUENCY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-Annually' },
  { value: 'annual', label: 'Annually' },
  { value: 'asneeded', label: 'As Needed' }
]

const EMPTY_ROUTE_OPTIONS = []

// Access hours are stored as minutes-from-midnight; the modal edits them as
// HH:MM <input type="time"> strings.
const minutesToTimeString = (minutes) => {
  if (minutes == null || !Number.isFinite(Number(minutes))) return ''
  const total = Math.round(Number(minutes))
  const hh = String(Math.floor(total / 60)).padStart(2, '0')
  const mm = String(total % 60).padStart(2, '0')
  return `${hh}:${mm}`
}

const timeStringToMinutes = (value) => {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2])
}

// Reference-hint formatting for an existing RestrictedArea (display-only gate
// hours on Jobsite, unrelated storage) — mirrors RestrictedAreasSection.js's
// formatAccessHours/getActiveDays/hasAccessSchedule verbatim.
const formatAccessHours = (hours) => {
  if (!hours?.start || !hours?.end) return 'Hours not set'
  return `${hours.start} - ${hours.end}`
}

const getActiveDays = (days) => {
  if (!days) return 'All days'
  const activeDays = Object.entries(days)
    .filter(([, active]) => active)
    .map(([day]) => day.slice(0, 3).toUpperCase())
  if (activeDays.length === 7) return 'All days'
  if (activeDays.length === 0) return 'No days'
  return activeDays.join(', ')
}

const hasAccessSchedule = (restrictionDetails = {}) => {
  const hasHours = Boolean(restrictionDetails.accessHours?.start || restrictionDetails.accessHours?.end)
  const hasLimitedDays = restrictionDetails.accessDays
    ? Object.values(restrictionDetails.accessDays).some(active => !active)
    : false
  return hasHours || hasLimitedDays || ['time-based', 'open-hours'].includes(restrictionDetails.restrictionType)
}

const getDefaultCrewSize = (property = {}) => {
  const size = Number(property.assignedRouteCrewSize || property.prescribedCrewSize || property.internalEstimateCrewCount || property.crewSize || 3)
  return Number.isFinite(size) && size > 0 ? size : 3
}

const formatHours = (value) => {
  const hours = Number(value || 0)
  if (!hours) return '0h'
  const roundedMinutes = Math.round(hours * 60)
  const wholeHours = Math.floor(roundedMinutes / 60)
  const minutes = roundedMinutes % 60
  if (!wholeHours) return `${minutes}m`
  if (!minutes) return `${wholeHours}h`
  return `${wholeHours}h ${minutes}m`
}

const formatDecimal = (value) => {
  const num = Number(value || 0)
  if (!num) return ''
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

const selectOnFocus = (event) => {
  event.target.select()
}

const getBillingGroupPropertyId = (property = {}) => {
  const id = property.aspirePropertyId || property.propertyId || property.PropertyID
  const numeric = Number(id)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

// Combine the picker's member ids with the auto-included current property,
// then dedupe and sort. The picker already excludes the current property and
// non-numeric values, so this is mainly the "pin current property" step.
const buildBillingGroupMemberIds = (memberIds, currentPropertyId) => {
  const ids = (Array.isArray(memberIds) ? memberIds : [])
    .map(id => Number(id))
    .filter(id => Number.isFinite(id) && id > 0)
  if (currentPropertyId) ids.push(currentPropertyId)
  return [...new Set(ids)].sort((a, b) => a - b)
}

export const getLockRouteId = (route = {}) => {
  if (route?._id) return String(route._id)
  if (String(route?.id || '').startsWith('route:')) return String(route.id).slice(6)
  const routeId = String(route?.routeId || '')
  return /^[a-f\d]{24}$/i.test(routeId) ? routeId : ''
}

export const getLockRouteCrewId = (route = {}) => {
  const crewId = route?.crewId || route?.crew?._id || route?.crew || null
  return crewId ? String(crewId) : null
}

const getRouteOptionLabel = (route = {}) => {
  return route.displayName || route.name || route.routeName || route.crewName || route.crewLeaderName || ''
}

export function PropertyEditForm ({ property, onSave, crews, routes = EMPTY_ROUTE_OPTIONS, url = '', propertyOptions = [], onClose, onSaved, onCancel, cancelLabel = 'Cancel', onDirtyChange, visibleSections = null, hideTitle = false }) {
  const [prescribedMode, setPrescribedMode] = useState('man')
  const [prescribedManHours, setPrescribedManHours] = useState('')
  const [prescribedClockHours, setPrescribedClockHours] = useState('')
  const [prescribedCrewSize, setPrescribedCrewSize] = useState('3')
  const [mtcFrequency, setMtcFrequency] = useState('none')
  const [irrigationFrequency, setIrrigationFrequency] = useState('none')
  const [herbicideFrequency, setHerbicideFrequency] = useState('none')
  const [mowingRequired, setMowingRequired] = useState(false)
  const [geofenceSet, setGeofenceSet] = useState(false)
  const [propertyLinesVerified, setPropertyLinesVerified] = useState(false)
  const [takeoffFinished, setTakeoffFinished] = useState(false)
  const [takeoffNotes, setTakeoffNotes] = useState('')
  const [lockToRoute, setLockToRoute] = useState(null)
  const [lockToRouteTouched, setLockToRouteTouched] = useState(false)
  const [preferredDays, setPreferredDays] = useState([])
  const [requiredDays, setRequiredDays] = useState([])
  const [serviceDaysTouched, setServiceDaysTouched] = useState(false)
  const [accessEarliestTime, setAccessEarliestTime] = useState('')
  const [accessLatestTime, setAccessLatestTime] = useState('')
  const [restrictedAreasHint, setRestrictedAreasHint] = useState([])
  const [loadedRoutes, setLoadedRoutes] = useState([])
  const [routesLoading, setRoutesLoading] = useState(false)
  const [irrigationTech, setIrrigationTech] = useState(null)
  const [noIrrigation, setNoIrrigation] = useState(false)
  const [billingGroupId, setBillingGroupId] = useState(null)
  const [billingGroupName, setBillingGroupName] = useState('')
  const [billingGroupMemberIds, setBillingGroupMemberIds] = useState([])
  const [billingGroupNotes, setBillingGroupNotes] = useState('')
  const [billingGroupError, setBillingGroupError] = useState('')
  const [billingGroupConflicts, setBillingGroupConflicts] = useState(null)
  const [saving, setSaving] = useState(false)
  const [seedNonce, setSeedNonce] = useState(0)

  // Seed the form from the property on mount and whenever property/crews/routes
  // change. Callers should pass memoized crews/routes arrays (all current
  // callers do) so this does not re-seed and clobber in-progress edits.
  useEffect(() => {
    const p = property || {}
    const crewList = crews || []
    const routeList = routes || []
    const inferred = inferPropertyEditDefaults(p)
    const crewSize = getDefaultCrewSize(p)
    const manHours = p.prescribedManHours != null ? Number(p.prescribedManHours) : 0
    setPrescribedMode('clock')
    setPrescribedCrewSize(formatDecimal(crewSize))
    setPrescribedManHours(manHours ? formatDecimal(manHours) : '')
    setPrescribedClockHours(manHours ? formatDecimal(manHours / crewSize) : '')
    setMtcFrequency(inferred.mtcFrequency)
    setIrrigationFrequency(inferred.irrigationCheckFrequency)
    setHerbicideFrequency(inferred.herbicideApplicationFrequency)
    setMowingRequired(!!p.mowingEnabled)
    // Human-confirmed setup checklist (geofence / property lines / takeoff). Each
    // rides on p.setupChecklist.<item>.confirmed; takeoff notes come off the item.
    const cl = p.setupChecklist || {}
    setGeofenceSet(!!cl.geofenceSet?.confirmed)
    setPropertyLinesVerified(!!cl.propertyLinesVerified?.confirmed)
    setTakeoffFinished(!!cl.takeoffComplete?.confirmed)
    setTakeoffNotes(cl.takeoffComplete?.notes || '')
    const lockedRouteId = String(p.lockedRouteId || p.lockToRouteId || '')
    const lockedCrewId = String(p.lockedCrewId || p.lockToCrewId || '')
    setLockToRoute(
      routeList.find(route => lockedRouteId && getLockRouteId(route) === lockedRouteId) ||
      routeList.find(route => lockedCrewId && getLockRouteCrewId(route) === lockedCrewId) ||
      null
    )
    setLockToRouteTouched(false)
    setPreferredDays(Array.isArray(p.preferredDays) ? p.preferredDays : [])
    setRequiredDays(Array.isArray(p.requiredDays) ? p.requiredDays : [])
    setServiceDaysTouched(false)
    setAccessEarliestTime(minutesToTimeString(p.accessEarliestMin))
    setAccessLatestTime(minutesToTimeString(p.accessLatestMin))
    setIrrigationTech(p.irrigationTechId ? (crewList.find(c => c._id === p.irrigationTechId) || null) : null)
    setNoIrrigation(inferred.noIrrigation)
    setBillingGroupId(p.billingGroup?._id || null)
    setBillingGroupName(p.billingGroup?.name || '')
    const currentId = getBillingGroupPropertyId(p)
    setBillingGroupMemberIds(
      (Array.isArray(p.billingGroup?.memberAspirePropertyIds) ? p.billingGroup.memberAspirePropertyIds : [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0 && id !== currentId)
    )
    setBillingGroupNotes(p.billingGroup?.notes || '')
    setBillingGroupError('')
    setBillingGroupConflicts(null)
    setSaving(false)
    setSeedNonce(n => n + 1)
  }, [property, crews, routes])

  // Route lock options come from the canonical active Aspire-route endpoint.
  // Supplied options render immediately; the fetch fills gaps such as empty
  // routes that do not yet appear in a scenario's Refine analysis.
  useEffect(() => {
    if (!url) return undefined
    let cancelled = false
    setRoutesLoading(true)
    axios.get(`${url}/api/hmi/territory/routes`)
      .then(response => {
        if (cancelled) return
        const available = (response.data?.routes || []).filter(route => (
          route.isActive !== false && route.excludeFromTerritory !== true && getLockRouteCrewId(route)
        ))
        setLoadedRoutes(available)
      })
      .catch(() => {
        if (!cancelled) setLoadedRoutes([])
      })
      .finally(() => {
        if (!cancelled) setRoutesLoading(false)
      })
    return () => { cancelled = true }
  }, [url])

  // Read-only reference: any existing RestrictedArea (gate/polygon hours,
  // display-only, unrelated storage) on this property's jobsite, so the
  // operator can see a pre-existing gate restriction while setting the
  // solver-facing access window below. No write-back, no pre-fill.
  useEffect(() => {
    if (!url || !property?.jobsiteId) { setRestrictedAreasHint([]); return undefined }
    let cancelled = false
    axios.get(`${url}/api/customer/restrictedAreas?jobSiteId=${property.jobsiteId}`)
      .then(response => {
        if (cancelled) return
        setRestrictedAreasHint(response.data?.restrictedAreas || [])
      })
      .catch(() => {
        if (!cancelled) setRestrictedAreasHint([])
      })
    return () => { cancelled = true }
  }, [url, property])

  const routeOptions = useMemo(() => {
    // Scenario/live callers provide their own eligible route set. The canonical
    // fetch is a fallback for property screens that do not already have routes.
    const source = (routes || []).length ? routes : loadedRoutes
    const unique = new Map()
    source.forEach(route => {
      const id = getLockRouteId(route)
      if (id && getLockRouteCrewId(route) && !unique.has(id)) unique.set(id, route)
    })
    return [...unique.values()].sort((a, b) => getRouteOptionLabel(a).localeCompare(getRouteOptionLabel(b)))
  }, [loadedRoutes, routes])

  // Everything here feeds the save payload. lockToRoute/service days are
  // covered by their touched flags below (route-lock hydration would
  // otherwise read as dirty); prescribedMode is display-only.
  const formFingerprint = JSON.stringify({
    prescribedManHours,
    mtcFrequency,
    irrigationFrequency,
    herbicideFrequency,
    mowingRequired,
    geofenceSet,
    propertyLinesVerified,
    takeoffFinished,
    takeoffNotes,
    accessEarliestTime,
    accessLatestTime,
    noIrrigation,
    irrigationTechId: irrigationTech?._id || null,
    billingGroupName,
    billingGroupMemberIds,
    billingGroupNotes
  })

  const seededFingerprintRef = useRef(formFingerprint)
  // Deps deliberately omit formFingerprint: this snapshots it only at the
  // seedNonce transition (re-seed), never on ordinary edits.
  useEffect(() => {
    seededFingerprintRef.current = formFingerprint
  }, [seedNonce])

  useEffect(() => {
    if (!onDirtyChange) return
    onDirtyChange(formFingerprint !== seededFingerprintRef.current || lockToRouteTouched || serviceDaysTouched)
  }, [formFingerprint, lockToRouteTouched, serviceDaysTouched, seedNonce, onDirtyChange])

  // If the canonical route fetch arrives after the modal opens, hydrate only
  // the saved route lock. Do not re-seed prescribed hours/frequencies and wipe
  // edits the operator may already have made while routes were loading.
  useEffect(() => {
    if (!routeOptions.length) return
    const lockedRouteId = String(property?.lockedRouteId || property?.lockToRouteId || '')
    const lockedCrewId = String(property?.lockedCrewId || property?.lockToCrewId || '')
    if (!lockedRouteId && !lockedCrewId) return
    const saved = routeOptions.find(route => lockedRouteId && getLockRouteId(route) === lockedRouteId) ||
      routeOptions.find(route => lockedCrewId && getLockRouteCrewId(route) === lockedCrewId) ||
      null
    if (saved) setLockToRoute(saved)
  }, [property, routeOptions])

  const handleSave = async (force = false) => {
    const crewSize = Number(prescribedCrewSize || 0)
    const clockHours = Number(prescribedClockHours || 0)
    const manHours = prescribedMode === 'clock'
      ? (clockHours > 0 && crewSize > 0 ? clockHours * crewSize : null)
      : (prescribedManHours === '' ? null : Number(prescribedManHours))
    const billingGroupMembers = buildBillingGroupMemberIds(billingGroupMemberIds, getBillingGroupPropertyId(property || {}))
    const hasName = !!billingGroupName.trim()
    const hasMembers = (Array.isArray(billingGroupMemberIds) ? billingGroupMemberIds.length : 0) > 0
    // Treat members-without-a-name as an in-progress group rather than silently
    // dropping it on save.
    if (hasMembers && !hasName) {
      setBillingGroupError('Give the billing group a name to save it.')
      return
    }
    const shouldSaveBillingGroup = hasName
    if (shouldSaveBillingGroup && billingGroupMembers.length < 2) {
      setBillingGroupError('Add at least one other property to the group.')
      return
    }
    const payload = {
      prescribedManHours: manHours == null ? null : Math.round(manHours * 100) / 100,
      mtcFrequency,
      irrigationCheckFrequency: irrigationFrequency,
      herbicideApplicationFrequency: herbicideFrequency,
      mowingEnabled: mowingRequired,
      setupChecklist: {
        geofenceSet: { confirmed: geofenceSet },
        propertyLinesVerified: { confirmed: propertyLinesVerified },
        takeoffComplete: { confirmed: takeoffFinished, notes: takeoffNotes.trim() || null }
      },
      irrigationTechId: noIrrigation ? null : (irrigationTech?._id || null),
      noIrrigation,
      accessEarliestMin: timeStringToMinutes(accessEarliestTime),
      accessLatestMin: timeStringToMinutes(accessLatestTime),
      billingGroup: shouldSaveBillingGroup
        ? {
            _id: billingGroupId || null,
            name: billingGroupName.trim(),
            memberAspirePropertyIds: billingGroupMembers,
            notes: billingGroupNotes.trim(),
            active: true
          }
        : null
    }
    if (lockToRouteTouched) {
      payload.lockedRouteId = lockToRoute ? getLockRouteId(lockToRoute) : null
      payload.lockedCrewId = lockToRoute ? getLockRouteCrewId(lockToRoute) : null
    }
    if (serviceDaysTouched) {
      payload.preferredDays = preferredDays
      payload.requiredDays = requiredDays
    }
    setSaving(true)
    setBillingGroupError('')
    try {
      // Await so server-side billing errors (e.g. overlap) surface in the modal
      // instead of being swallowed after an early close.
      if (onSave) await onSave(payload, { force })
      ;(onSaved || onClose)()
    } catch (err) {
      const conflicts = getBillingGroupOverlapConflicts(err)
      if (conflicts) {
        setBillingGroupConflicts(conflicts)
        setBillingGroupError('')
      } else {
        setBillingGroupConflicts(null)
        setBillingGroupError(err?.response?.data?.message || err?.message || 'Failed to save billing group.')
      }
    } finally {
      setSaving(false)
    }
  }

  const labelStyle = { fontSize: 12, fontWeight: 600, color: V4_T.ink2, marginBottom: 4, display: 'block' }
  const fieldWrap = { marginBottom: 16 }
  const currentCrewSize = Number(prescribedCrewSize || 0) || getDefaultCrewSize(property || {})
  const currentManHours = prescribedMode === 'clock'
    ? (Number(prescribedClockHours || 0) * currentCrewSize)
    : Number(prescribedManHours || 0)
  const currentClockHours = currentCrewSize > 0 ? currentManHours / currentCrewSize : 0

  const renderFrequency = (label, value, setValue) => (
    <div style={fieldWrap}>
      <span style={labelStyle}>{label}</span>
      <FormControl fullWidth size="small" variant="outlined">
        <Select value={value} onChange={(e) => setValue(e.target.value)}>
          {PROPERTY_FREQUENCY_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )

  const getCrewOptionLabel = (c) => c?.name || c?.foremanName || ''
  const getCrewOptionSelected = (o, v) => o?._id === v?._id
  const getRouteOptionSelected = (o, v) => getLockRouteId(o) === getLockRouteId(v)

  const togglePreferredDay = (day) => {
    setServiceDaysTouched(true)
    setPreferredDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  }

  const toggleRequiredDay = (day) => {
    setServiceDaysTouched(true)
    setRequiredDays(prev => {
      if (prev.includes(day)) return prev.filter(d => d !== day)
      return [...prev, day]
    })
    // A day can't be both required and preferred; required wins, so drop it
    // from preferred when it becomes required.
    setPreferredDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : prev)
  }

  // Presentation-only slicing for the review panel's tabs. Hidden sections
  // keep their state (it lives on this component) and still save. The billing
  // section force-renders while it has an active error so a billing save
  // failure is never invisible behind an inactive tab.
  const showSection = (key) => {
    if (key === 'billing' && (billingGroupError || billingGroupConflicts)) return true
    return !visibleSections || visibleSections.includes(key)
  }

  return (
    <>
      {!hideTitle && (
        <DialogTitle style={{ borderBottom: `1px solid ${V4_T.line}`, paddingBottom: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: V4_T.ink }}>Edit Property</span>
            <span style={{ fontSize: 12, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 2 }}>
              {property?.name || property?.propertyName || 'Property'}
            </span>
          </div>
        </DialogTitle>
      )}
      <DialogContent style={{ paddingTop: 16 }}>
        {showSection('prescription') && (
        <div style={fieldWrap}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
            <span style={{ ...labelStyle, marginBottom: 0 }}>Prescribed Time</span>
            <div style={{ display: 'flex', border: `1px solid ${V4_T.line}`, borderRadius: 6, overflow: 'hidden' }}>
              {[
                ['man', 'Man-hours'],
                ['clock', 'Clock time']
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPrescribedMode(value)}
                  style={{
                    border: 'none',
                    borderRight: value === 'man' ? `1px solid ${V4_T.line}` : 'none',
                    background: prescribedMode === value ? V4_T.green : '#fff',
                    color: prescribedMode === value ? '#fff' : V4_T.ink2,
                    padding: '5px 9px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {prescribedMode === 'man' ? (
            <TextField
              type="number"
              fullWidth
              size="small"
              variant="outlined"
              value={prescribedManHours}
              onFocus={selectOnFocus}
              onChange={(e) => {
                const next = e.target.value
                setPrescribedManHours(next)
                const crewSize = Number(prescribedCrewSize || 0)
                setPrescribedClockHours(next !== '' && crewSize > 0 ? formatDecimal(Number(next) / crewSize) : '')
              }}
              placeholder="e.g. 9"
              inputProps={{ min: 0, step: 0.25 }}
            />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="outlined"
                label="Clock hours"
                value={prescribedClockHours}
                onFocus={selectOnFocus}
                onChange={(e) => {
                  const next = e.target.value
                  setPrescribedClockHours(next)
                  const crewSize = Number(prescribedCrewSize || 0)
                  setPrescribedManHours(next !== '' && crewSize > 0 ? formatDecimal(Number(next) * crewSize) : '')
                }}
                placeholder="e.g. 2.25"
                inputProps={{ min: 0, step: 0.25 }}
              />
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="outlined"
                label="Crew"
                value={prescribedCrewSize}
                onFocus={selectOnFocus}
                onChange={(e) => {
                  const next = e.target.value
                  setPrescribedCrewSize(next)
                  const crewSize = Number(next || 0)
                  const clockHours = Number(prescribedClockHours || 0)
                  setPrescribedManHours(clockHours > 0 && crewSize > 0 ? formatDecimal(clockHours * crewSize) : prescribedManHours)
                }}
                inputProps={{ min: 1, step: 1 }}
              />
            </div>
          )}
          <div style={{ marginTop: 7, fontSize: 11, color: V4_T.muteSoft, lineHeight: 1.4 }}>
            Saves as <strong>{formatDecimal(currentManHours) || '0'} man-hours</strong>.
            {currentCrewSize > 0 && currentManHours > 0
              ? ` At ${formatDecimal(currentCrewSize)} crew, that is about ${formatHours(currentClockHours)} clock time.`
              : ' Enter either man-hours or clock time with crew size.'}
          </div>
        </div>
        )}

        {showSection('frequencies') && (
          <>
            {renderFrequency('Maintenance Frequency', mtcFrequency, setMtcFrequency)}
            {renderFrequency('Irrigation Check Frequency', irrigationFrequency, setIrrigationFrequency)}
            {renderFrequency('Herbicide Application Frequency', herbicideFrequency, setHerbicideFrequency)}
          </>
        )}

        {showSection('routeLock') && (
        <div style={fieldWrap}>
          <span style={labelStyle}>Lock to Route</span>
          <Autocomplete
            options={routeOptions}
            loading={routesLoading}
            getOptionLabel={getRouteOptionLabel}
            getOptionSelected={getRouteOptionSelected}
            value={lockToRoute}
            onChange={(_, val) => {
              setLockToRoute(val)
              setLockToRouteTouched(true)
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" placeholder="Any route (unlocked)" />
            )}
          />
          <div style={{ marginTop: 5, fontSize: 10.5, color: V4_T.muteSoft }}>
            Locked properties stay assigned to this route during moves and optimization.
          </div>
        </div>
        )}

        {showSection('serviceDays') && (
        <div style={fieldWrap}>
          <span style={labelStyle}>Service Days</span>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: V4_T.muteSoft, marginBottom: 4 }}>Preferred days</div>
            <div role="group" aria-label="Preferred days" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SERVICE_DAYS.map(({ value, label }) => {
                const selected = preferredDays.includes(value)
                return (
                  <Chip
                    key={`preferred-${value}`}
                    label={label}
                    size="small"
                    clickable
                    onClick={() => togglePreferredDay(value)}
                    variant={selected ? 'default' : 'outlined'}
                    aria-pressed={selected}
                    // Soft tier, soft fill — mirrors the drawer's v4GreenChip
                    // treatment; the Required row below uses the solid fill.
                    style={selected ? { background: V4_T.greenSoft, color: V4_T.greenDeep, border: `1px solid ${V4_T.green}` } : undefined}
                    disabled={requiredDays.includes(value)}
                  />
                )
              })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: V4_T.muteSoft, marginBottom: 4 }}>Required days</div>
            <div role="group" aria-label="Required days" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SERVICE_DAYS.map(({ value, label }) => {
                const selected = requiredDays.includes(value)
                return (
                  <Chip
                    key={`required-${value}`}
                    label={label}
                    size="small"
                    clickable
                    onClick={() => toggleRequiredDay(value)}
                    variant={selected ? 'default' : 'outlined'}
                    aria-pressed={selected}
                    style={selected ? { background: V4_T.green, color: '#fff' } : undefined}
                  />
                )
              })}
            </div>
          </div>
          <div style={{ marginTop: 7, fontSize: 10.5, color: V4_T.muteSoft, lineHeight: 1.4 }}>
            Required: must be serviced on these days — the optimizer moves other properties to make room. Preferred: honored when capacity allows.
          </div>
        </div>
        )}

        {showSection('accessHours') && (
        <div style={fieldWrap}>
          <span style={labelStyle}>Access Hours</span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <TextField
              id="access-earliest-arrival"
              type="time"
              fullWidth
              size="small"
              variant="outlined"
              label="Earliest arrival"
              InputLabelProps={{ shrink: true }}
              value={accessEarliestTime}
              onChange={(e) => setAccessEarliestTime(e.target.value)}
            />
            <TextField
              id="access-latest-departure"
              type="time"
              fullWidth
              size="small"
              variant="outlined"
              label="Latest departure"
              InputLabelProps={{ shrink: true }}
              value={accessLatestTime}
              onChange={(e) => setAccessLatestTime(e.target.value)}
            />
          </div>
          <div style={{ marginTop: 7, fontSize: 10.5, color: V4_T.muteSoft, lineHeight: 1.4 }}>
            Crew cannot arrive before / must leave by these times. Leave blank if unrestricted.
          </div>
          {restrictedAreasHint.filter(area => hasAccessSchedule(area.restrictionDetails)).map(area => (
            <div key={area._id || area.name} style={{ marginTop: 6, fontSize: 10.5, color: V4_T.muteSoft, lineHeight: 1.4 }}>
              Note: '{area.name}' restricted area on file — access {formatAccessHours(area.restrictionDetails?.accessHours)} ({getActiveDays(area.restrictionDetails?.accessDays)}).
            </div>
          ))}
        </div>
        )}

        {showSection('irrigation') && (
          <>
        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={noIrrigation}
                onChange={(e) => {
                  setNoIrrigation(e.target.checked)
                  if (e.target.checked) setIrrigationTech(null)
                }}
                color="primary"
              />
            }
            label={
              <span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>
                {noIrrigation ? 'Property Has No Irrigation' : 'Property Has Irrigation (Requires Inspections)'}
              </span>
            }
          />
        </div>

        {!noIrrigation && (
          <div style={fieldWrap}>
            <span style={labelStyle}>Assigned Irrigation Tech</span>
            <Autocomplete
              options={crews || []}
              getOptionLabel={getCrewOptionLabel}
              getOptionSelected={getCrewOptionSelected}
              value={irrigationTech}
              onChange={(_, val) => setIrrigationTech(val)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="No tech assigned" />
              )}
            />
          </div>
        )}
          </>
        )}

        {showSection('mowing') && (
        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <FormControlLabel
            control={<Switch checked={mowingRequired} onChange={(e) => setMowingRequired(e.target.checked)} color="primary" />}
            label={<span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>Mowing Required</span>}
          />
        </div>
        )}

        {showSection('checklist') && (
        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <span style={labelStyle}>Setup Checklist</span>
          <ConfirmRow
            label="Geofence set"
            checked={geofenceSet}
            onChange={setGeofenceSet}
            stamp={property?.setupChecklist?.geofenceSet}
          />
          <ConfirmRow
            label="Property lines verified"
            checked={propertyLinesVerified}
            onChange={setPropertyLinesVerified}
            stamp={property?.setupChecklist?.propertyLinesVerified}
          />
          <ConfirmRow
            label="Takeoff complete"
            checked={takeoffFinished}
            onChange={setTakeoffFinished}
            stamp={property?.setupChecklist?.takeoffComplete}
          />
          {takeoffFinished && (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={takeoffNotes}
              onChange={(e) => setTakeoffNotes(e.target.value)}
              placeholder="Takeoff notes (optional)"
              style={{ marginTop: 8 }}
            />
          )}
        </div>
        )}

        {showSection('billing') && (
        <div style={{ borderTop: `1px solid ${V4_T.line}`, marginTop: 16, paddingTop: 16 }}>
          <span style={labelStyle}>Billing Group</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={billingGroupName}
              onChange={(e) => {
                setBillingGroupName(e.target.value)
                setBillingGroupError('')
              }}
              placeholder="Group name"
            />
            <BillingGroupMemberPicker
              value={billingGroupMemberIds}
              onChange={(ids) => {
                setBillingGroupMemberIds(ids)
                setBillingGroupError('')
                setBillingGroupConflicts(null)
              }}
              currentPropertyId={getBillingGroupPropertyId(property || {})}
              propertyOptions={propertyOptions}
              error={!!billingGroupError}
              helperText={billingGroupError || undefined}
            />
            <BillingGroupConflictNotice
              conflicts={billingGroupConflicts}
              saving={saving}
              onConfirm={() => handleSave(true)}
              onCancel={() => setBillingGroupConflicts(null)}
            />
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={billingGroupNotes}
              onChange={(e) => setBillingGroupNotes(e.target.value)}
              placeholder="Notes"
              multiline
              minRows={2}
            />
          </div>
        </div>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid ${V4_T.line}`, padding: '12px 24px' }}>
        <Button onClick={onCancel || onClose} disabled={saving} style={{ color: V4_T.ink2 }}>{cancelLabel}</Button>
        <Button
          variant="contained"
          onClick={() => handleSave(false)}
          disabled={saving}
          style={{ fontWeight: 600, background: V4_T.green, color: '#fff' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </>
  )
}

// Thin Dialog host for existing callers. `open` gates mounting so the form's
// effects only run while the dialog is up — same behavior as the old
// open-guarded effects. Content unmounts at close (skips the fade-out).
function PropertyEditModal ({ open, onClose, ...formProps }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {open && <PropertyEditForm {...formProps} onClose={onClose} />}
    </Dialog>
  )
}

// One setup-checklist confirm row: a switch + the audit stamp (who/when) when
// the item has been confirmed. `stamp` is the { confirmed, confirmedAt, confirmedBy }
// subdoc from the property's setupChecklist.
function ConfirmRow ({ label, checked, onChange, stamp }) {
  return (
    <div style={{ marginBottom: 2 }}>
      <FormControlLabel
        control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} color="primary" />}
        label={<span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>{label}</span>}
      />
      {stamp?.confirmedAt && (
        <div style={{ fontSize: 11, color: V4_T.mute, marginLeft: 4, marginTop: -2 }}>
          Confirmed {new Date(stamp.confirmedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          {stamp.confirmedBy ? ` · ${stamp.confirmedBy}` : ''}
        </div>
      )}
    </div>
  )
}

export default PropertyEditModal
